import * as React from "react";
import * as ReactDOM from "react-dom";
import * as utils from "../../utils/utils";
import { ComboBoxType, DropDownTextItem } from "./";
import { DropDown } from "./dropDown";
import { VirtualList } from "../virtualList";
import { InputBase, InputContainer, InputHint, IInputBaseProperties, InputSize } from "../input";
import { PopupProvider, Popup, PopupTrigger, PopupAlign } from "../popup";

interface IProperties extends IInputBaseProperties
{
    /**
     * Enthält alle Child-Elemente.
     */
    children: (item) => any,

    /**
     * Inline-Style.
     */
    style?: React.CSSProperties,

    /**
     * Ein Text der als Label dargestellt wird.
     */
    label?: string,

    /**
     * Die Daten die im DropDown angezeigt werden sollen.
     */
    dataSource: any[],

    /**
     * Der Pfad zum tatsächlichen Wert in der DataSource.
     */
    valueMember: string,

    /**
     * Der Pfad zum Anzeigewert in der DataSource.
     */
    displayMember: string,

    /**
     * Enthält den selektierten Wert.
     */
    selectedValue?: any,

    /**
     * Gibt an, was für eine Art von ComboBox gerendert werden soll.
     */
    comboBoxType?: ComboBoxType,

    /**
     * Gibt die maximale Größe des DropDown Menüs an.
     */
    dropDownMaxHeight?: number,

    /**
     * Gibt die Höhe eines einzelnen DropDownMenuItems an. Wird für die @see VirtualList benötigt.
     */
    dropDownMenuItemHeight: number,

    /**
     * Gibt an, ob der Button zum öffnen des DropDown Menüs ausgeblendet werden soll.
     */
    hideDropDownMenuButton?: boolean,

    /**
     * Enthält die @see InputSideButtons die neben der ComboBox dargestellt werden sollen.
     */
    sideButtons?: JSX.Element[],

    /**
     * Angabe, ob per "ENTF"-Taste die selektion entfernt werden kann.
     */
    canRemoveSelection?: boolean,

    /**
     * Setzt einen Hilfetext.
     */
    hint?: string | JSX.Element

    /**
     * Wird ausgeführt, sobald sich das selektierte Item ändert.
     */
    onSelectionChanged?: (selectedItem: any) => void
}

interface IState
{
    dataSource: any[];
    selectedValue: any;
    inputText: string;
    errorMessage: string;
    isFocused: boolean;
    isDropDownOpen: boolean;
    scrollToIndex: number;
}

export class ComboBox extends React.Component<IProperties, IState>
{
    /**
     * Enthält eine Referenz zum Container DOM-Element dieser @see ComboBox Komponente.
     */
    private _comboboxContainerRef: HTMLElement;

    /**
     * Enthält eine Referenz zum DOM-Element dieser @see ComboBox Komponente.
     */
    private _comboboxRef: HTMLElement;

    /**
     * Enthält eine Referenz zum DOM-Element der @see InputBase Komponente.
     */
    private _inputRef: HTMLInputElement | HTMLTextAreaElement;

    /**
     * Enthält eine Referenz zum DOM-Element des DropDownButton dieser @see ComboBox Komponente.
     */
    private _buttonRef: HTMLElement;

    /**
     * Enthält eine Referenz zur Funktion die zum schließen des DropDowns verwendet wird.
     */
    private _hideFunc: () => void;

    /**
     * Enthält alle gerenderten Optionen des DropDown Menüs.
     */
    private _childs: JSX.Element[];

    private _pressedKeys: string[];
    private _clearKeys: boolean;

    /**
     * Standardwerte setzen.
     */
    public static defaultProps: Partial<IProperties> = {
        comboBoxType: ComboBoxType.dropDownList,
        dropDownMaxHeight: 200
    };

    /**
     * Initialisiert die Komponente @see ComboBox.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);

        this._hideFunc = this.hide.bind(this);
        this.state = {
            dataSource: props.dataSource,
            selectedValue: props.selectedValue,
            inputText: props.value,
            errorMessage: props.errorMessage,
            isFocused: false,
            isDropDownOpen: false,
            scrollToIndex: null
        };
    }

    /**
     * Wird ausgeführt, sobald die Komponente neue Eigenschaften erhält.
     * Hier werden die benötigten Daten erneut geladen.
     * @param nextProps Die nächsten/neuen Eigenschaften.
     */
    componentWillReceiveProps(nextProps: IProperties)
    {
        const oldProp = this.props;
        const newProp = nextProps;

        this._hideFunc = this.hide.bind(this);
        this.setState({
            dataSource: newProp.dataSource,
            selectedValue: newProp.selectedValue,
            inputText: newProp.value,
            errorMessage: newProp.errorMessage
        });
    }

    /**
     * Wird ausgeführt, sobald diese Komponente im DOM platziert wurde.
     */
    componentDidMount()
    {
        window.addEventListener("resize", this._hideFunc);
        window.addEventListener("contextmenu", this._hideFunc);
        window.addEventListener("click", this._hideFunc);
        window.addEventListener("scroll", this._hideFunc);
        window.addEventListener("blur", this._hideFunc);
        window.addEventListener("keyup", this._hideFunc);
    }

    /**
     * Wird ausgeführt, sobald diese Komponente aus dem DOM entfernt wurde.
     */
    componentWillUnmount()
    {
        window.removeEventListener("resize", this._hideFunc);
        window.removeEventListener("contextmenu", this._hideFunc);
        window.removeEventListener("click", this._hideFunc);
        window.removeEventListener("scroll", this._hideFunc);
        window.removeEventListener("blur", this._hideFunc);
        window.removeEventListener("keyup", this._hideFunc);
    }

    /**
     * Prüft ob das DropDown Menü geschlossen werden muss.
     * @param e Das Ereignis welches diese Funtkion ausgelöst hat.
     */
    private hide(e: Event)
    {
        switch (e.type)
        {
            case "click":
            case "contextmenu":
                {
                    if (this.state.isDropDownOpen)
                    {
                        var comboboxButton: JQuery<EventTarget> = $(e.target).closest(".combobox_button");
                        if (this._inputRef !== (e.target as HTMLInputElement) && this._comboboxRef != (e.target as HTMLElement) && !(comboboxButton.length > 0 && this._buttonRef === comboboxButton.get(0)))
                        {
                            this.setState({
                                isDropDownOpen: false,
                                dataSource: this.props.dataSource,
                                selectedValue: (this.props.comboBoxType === ComboBoxType.dropDown ? null : this.state.selectedValue)
                            });
                        }
                    }
                }
                break;
            case "resize":
            case "scroll":
            case "blur":
                {
                    this.setState({
                        isDropDownOpen: false,
                        dataSource: this.props.dataSource,
                        selectedValue: (this.props.comboBoxType === ComboBoxType.dropDown ? null : this.state.selectedValue)
                    });
                }
                break;
            case "keyup":
                {
                    if (this._inputRef !== document.activeElement)
                    {
                        var keyEvent = e as KeyboardEvent;
                        if (keyEvent.key === "Tab")
                        {
                            this.setState({
                                isDropDownOpen: false,
                                dataSource: this.props.dataSource,
                                selectedValue: (this.props.comboBoxType === ComboBoxType.dropDown ? null : this.state.selectedValue)
                            });
                        }
                    }
                }
                break;
        }
    }

    /**
     * Ermittelt die momentane Höhe des DropDown Menüs.
     */
    private getListHeight(): number
    {
        var virtualListHeight: number = this.props.dropDownMenuItemHeight * this.state.dataSource.length;
        return (virtualListHeight < this.props.dropDownMaxHeight) ? virtualListHeight : this.props.dropDownMaxHeight;
    }

    private callOnTextChange(value: any[], originalValue: string[], e: Event)
    {
        if (this.props.onTextChange != null)
            this.props.onTextChange(value, originalValue, e);
    }

    private callOnSelectionChanged(item: any)
    {
        if (this.props.onSelectionChanged != null)
            this.props.onSelectionChanged(item || {});
    }

    /**
     * Wird ausgeführt, sobald sich der Text des Inputs verändert.
     * Hier wird alles für die AutoComplete funktion eingerichtet.
     */
    private onTextChangedInputBase = (value: any[], originalValue: string[], e: Event) =>
    {
        // Den vom Benutzer eingetragenen Wert ermitteln.
        var inputValue = originalValue[0];

        var filterdSource: any[] = [];
        if (this.props.comboBoxType === ComboBoxType.dropDown)
        {
            filterdSource = this.props.dataSource.where((item) =>
            {
                let value = item[this.props.displayMember] as string;
                return value.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0;
            });

            this.setState({
                dataSource: filterdSource
            });
        }

        this.callOnTextChange(value, originalValue, e);
    }

    /**
     * Wird ausgeführt, sobald die @see InputBase Komponente fokusiert wird.
     */
    private onFocusInputBase()
    {
        if (this.props.comboBoxType === ComboBoxType.dropDown)
        {
            this.setState({
                isFocused: true
            });
        }
    }

    /**
     * Wird ausgeführt, sobald die ComboBox fokusiert wird.
     */
    private onFocusComboBox()
    {
        if (this.props.comboBoxType === ComboBoxType.dropDownList)
        {
            this.setState({
                isFocused: true
            });
        }
    }

    /**
     * Wird ausgeführt, sobald die @see InputBase Komponente den Fokus verliert.
     */
    private onBlurInputBase()
    {
        if (this.props.comboBoxType === ComboBoxType.dropDown)
        {
            this.setState({
                isFocused: false
            });
        }

        this._pressedKeys = null;
    }

    /**
     * Wird ausgeführt, sobald die ComboBox den Fokus verliert.
     */
    private onBlurComboBox = () =>
    {
        if (this.props.comboBoxType === ComboBoxType.dropDownList)
        {
            this.setState({
                isFocused: false
            });
        }
    }

    private onKeyDownInputBase = (e: React.KeyboardEvent<HTMLDivElement>) =>
    {
        if (e.key === "Backspace")
        {
            e.preventDefault();
            e.stopPropagation();
        }
    }

    /**
     * Wird ausgeführt, sobald bei der @see InputBase Komponente eine Taste losgelassen wird.
     */
    private onKeyUpInputBase(e: KeyboardEvent)
    {
        var isDropDownList: boolean = this.props.comboBoxType === ComboBoxType.dropDownList;
        var keyIsLetter: boolean = (e.key.length === 1 && /[\u00C0-\u017Fa-z0-9\s\.,]/i.test(e.key)) || false;
        var keyIsBackspace: boolean = e.key === "Backspace";
        var keyIsArrowUp: boolean = e.key === "ArrowUp";
        var keyIsArrowDown: boolean = e.key === "ArrowDown";

        // DropDown öffnen wenn es geschlossen ist.
        if (!this.state.isDropDownOpen && e.key != "Delete")
        {
            this.setState({
                isDropDownOpen: true
            }, () => this.onDropDownMenuOpened());
        }

        // Ermöglicht das auswählen einer Option mit den Pfeiltasten.
        var oldSelectedValue: any = this.state.selectedValue;
        var newSelectedValue: any;
        var newSelectedDisplayValue: string;
        var newItem: any;
        var scrollToIndex: number;

        if (keyIsArrowUp)
        {
            this._pressedKeys = null;
            let currentChild = this._childs.where(child => child.props.value === this.state.selectedValue).first();
            let newIndex = ((currentChild != null) ? currentChild.props.index - 1 : 0);
            newItem = this.state.dataSource.where((data, index) => index === newIndex).first();

            if (newItem != null)
            {
                newSelectedValue = newItem[this.props.valueMember];
                newSelectedDisplayValue = newItem[this.props.displayMember];
                scrollToIndex = newIndex;
            }

            e.preventDefault();
        }

        if (keyIsArrowDown)
        {
            this._pressedKeys = null;
            let currentChild = this._childs.where(child => child.props.value === this.state.selectedValue).first();
            let newIndex = ((currentChild != null) ? currentChild.props.index + 1 : 0);
            newItem = this.state.dataSource.where((data, index) => index === newIndex).first();

            if (newItem != null)
            {
                newSelectedValue = newItem[this.props.valueMember];
                newSelectedDisplayValue = newItem[this.props.displayMember];
                scrollToIndex = newIndex;
            }

            e.preventDefault();
        }

        if (keyIsBackspace)
        {
            e.preventDefault();
            e.stopPropagation();
        }

        if (isDropDownList && (keyIsLetter || keyIsBackspace))
        {
            this._pressedKeys = this._pressedKeys || [];

            if (!keyIsBackspace)
                this._pressedKeys.push(e.key);

            if (keyIsBackspace && this._pressedKeys.length > 0)
                this._pressedKeys.pop();

            var matchedIndexes: any[] = [];
            if (this._pressedKeys.length > 0)
            {
                var filterdSource = this.filterDataSource(matchedIndexes);
                newItem = filterdSource.first();

                if (newItem == null)
                {
                    this._pressedKeys.pop();

                    if (this._pressedKeys.length > 0)
                    {
                        filterdSource = this.filterDataSource(matchedIndexes);
                        newItem = filterdSource.first();
                    }
                }

                if (newItem != null)
                {
                    let index = matchedIndexes.where(m => m.id === newItem[this.props.valueMember]).first().index;
                    newSelectedValue = newItem[this.props.valueMember];
                    newSelectedDisplayValue = newItem[this.props.displayMember];
                    scrollToIndex = index < 0 ? 0 : index;
                }
            }
            else
            {
                this._comboboxRef.focus();
                this.setState({
                    selectedValue: null,
                    scrollToIndex: 0
                });

                if (oldSelectedValue !== newSelectedValue)
                    this.callOnSelectionChanged(newItem);
            }
        }

        if (newSelectedValue != null)
        {
            this.setState({
                selectedValue: newSelectedValue,
                scrollToIndex: scrollToIndex
            });

            if (!isDropDownList)
            {
                this.callOnTextChange([newSelectedDisplayValue], [newSelectedDisplayValue], e);
            }
            else
            {
                if (oldSelectedValue !== newSelectedValue)
                    this.callOnSelectionChanged(newItem);
            }

            this._inputRef.focus();
            this._inputRef.setSelectionRange(this._pressedKeys.length, newSelectedDisplayValue.length);
        }
        else if (isDropDownList && this.props.canRemoveSelection && e.key === "Delete")
        {
            this._comboboxRef.focus();
            this.setState({
                selectedValue: null,
                scrollToIndex: 0
            });

            if (oldSelectedValue !== newSelectedValue)
                this.callOnSelectionChanged(newItem);
        }

        setTimeout(() => this.setState({ scrollToIndex: null }), 50);

        // Wenn die Enter-Taste betätigt wird, wird der markierte Wert ausgewählt.
        if (e.key === "Enter")
        {
            this.setState({
                isDropDownOpen: false,
                dataSource: this.props.dataSource,
                selectedValue: (!isDropDownList ? null : this.state.selectedValue)
            });
        }
    }

    private filterDataSource(matchedIndexes)
    {
        return this.state.dataSource.where((item, index) =>
        {
            let value = item[this.props.displayMember] as string;
            if (value.toLowerCase().startsWith(this._pressedKeys.join("").toLowerCase()))
            {
                matchedIndexes.push({
                    id: item[this.props.valueMember],
                    index: index
                });
                return true;
            }

            return false;
        });
    }

    /**
     * Wird ausgeführt, sobald auf die @see ComboBox Komponente geklickt wird.
     */
    private onDropDownClick()
    {
        if (this.props.comboBoxType === ComboBoxType.dropDownList)
        {
            let isDropDownOpen = this.state.isDropDownOpen;
            this.setState({
                isFocused: true,
                isDropDownOpen: !isDropDownOpen
            }, () =>
                {
                    if (this.state.isDropDownOpen)
                        this.onDropDownMenuOpened()
                });
        }
    }

    /**
     * Wird ausgeführt, sobald auf den DropDownButton der @see ComboBox Komponente geklickt wird.
     */
    private onDropDownButtonClick()
    {
        if (this.props.comboBoxType === ComboBoxType.dropDown)
            this._inputRef.focus();

        if (this.state.isDropDownOpen)
        {
            this.setState({
                isFocused: this.props.comboBoxType === ComboBoxType.dropDown,
                isDropDownOpen: false,
                dataSource: this.props.dataSource,
                selectedValue: (this.props.comboBoxType === ComboBoxType.dropDown ? null : this.state.selectedValue)
            });
        }
        else
        {
            this.setState({
                isFocused: this.props.comboBoxType === ComboBoxType.dropDown,
                isDropDownOpen: true
            }, () => this.onDropDownMenuOpened());
        }
    }

    /**
     * Wird ausgeführt, sobald auf ein DropDownItem der @see ComboBox Komponente geklickt wird.
     */
    private onDropDownItemClicked = (value: any, displayValue: string, item?: any) =>
    {
        var oldValue = this.state.selectedValue;

        if (this.props.comboBoxType === ComboBoxType.dropDownList)
        {
            this.setState({
                selectedValue: value
            });
        }

        if (oldValue !== value)
            this.callOnSelectionChanged(item);
    }

    /**
     * Wird ausgeführt, sobald sich das DropDown Menü öffnet.
     */
    private onDropDownMenuOpened()
    {
        if (this.props.comboBoxType === ComboBoxType.dropDownList)
        {
            if (this.state.selectedValue != null)
            {
                let data = this.state.dataSource.where(data => data[this.props.valueMember] === this.state.selectedValue).first();
                if (data != null)
                {
                    let index: number = this.state.dataSource.indexOf(data);
                    this.setState({
                        scrollToIndex: index < 0 ? 1 : index + 1
                    }, () => setTimeout(() => this.setState({ scrollToIndex: null }), 50));
                }
            }
        }
    }

    /**
     * Erstellt die Label Komponente, wenn die Eigenschaft "label" gesetzt wurde.
     */
    private getLabel()
    {
        if (this.props.label != null)
        {
            return <label htmlFor={this.props.id}>{this.props.label}</label>
        }
    }

    /**
     * Erstellt den Button zum öffnen des DropDown Menüs.
     */
    private getDropDownButton()
    {
        if (!this.props.hideDropDownMenuButton)
        {
            return (
                <div
                    ref={(ref) => this._buttonRef = ref}
                    className="combobox_button"
                    onClick={this.onDropDownButtonClick.bind(this)}>
                    <i className="mdi mdi-chevron-down"></i>
                </div>
            );
        }

        return null;
    }

    /**
     * Ermittelt das DropDownMenuItem welches gerendert werden soll.
     * @param index Der momentane Index des Items.
     * @param data DataSource Item.
     * @param style Inline-Style. Wird für die @see VirtualList benötigt.
     */
    private getItem(index: number, data: any, style: any): JSX.Element
    {
        var child: JSX.Element = this.props.children(data);

        var props: any = {
            item: data,
            onClick: this.onDropDownItemClicked,
            index: index,
            style: style
        };

        if (this.props.comboBoxType === ComboBoxType.dropDown)
            props.boldDisplayValue = this.props.value;

        if (child.props.value === this.state.selectedValue)
            props.isSelected = true;

        var clone = React.cloneElement(child, props);

        this._childs.push(clone);
        return clone;
    }

    /**
     * Rendert diese Komponente.
     */
    render()
    {
        var className: string[] = ["combobox"]

        if (this.props.comboBoxType === ComboBoxType.dropDown)
            className.push("combobox--dropdown");
        else if (this.props.comboBoxType === ComboBoxType.dropDownList)
            className.push("combobox--dropdown-list");

        if (this.props.size === InputSize.small)
            className.push("combobox--small");
        else if (this.props.size === InputSize.normal)
            className.push("combobox--normal");

        if (this.state.isFocused)
            className.push("combobox--focused");

        if (this.state.isDropDownOpen)
            className.push("combobox--dropdown-open");

        if (this.props.isDisabled)
            className.push("combobox--disabled");

        this._childs = [];
        const dropDownHeight: number = this.getListHeight();

        return (
            <InputContainer style={{ position: "relative", ...this.props.style }}>
                {this.getLabel()}
                <div
                    className="combobox-container"
                    ref={(ref) => this._comboboxContainerRef = ref}>
                    <div
                        ref={(ref) => this._comboboxRef = ref}
                        tabIndex={0}
                        onFocus={this.onFocusComboBox.bind(this)}
                        onBlur={this.onBlurComboBox.bind(this)}
                        className={className.join(" ")}
                        title={this.props.errorMessage}
                        onClick={this.onDropDownClick.bind(this)}
                        onKeyDown={this.onKeyDownInputBase}
                        onKeyUp={this.props.comboBoxType === ComboBoxType.dropDownList ? this.onKeyUpInputBase.bind(this) : null}>
                        <InputBase
                            tabIndex={this.props.comboBoxType === ComboBoxType.dropDownList ? -1 : null}
                            inputRef={(ref) => this._inputRef = ref}
                            value={this.props.value}
                            onKeyUp={this.props.comboBoxType === ComboBoxType.dropDown ? this.onKeyUpInputBase.bind(this) : null}
                            onFocus={this.onFocusInputBase.bind(this)}
                            onBlur={this.onBlurInputBase.bind(this)}
                            {...this.props}
                            onTextChange={this.onTextChangedInputBase} />
                        {this.getDropDownButton()}
                    </div>
                    {this.props.sideButtons}
                </div>
                <DropDown
                    comboboxContainerRef={this._comboboxContainerRef}
                    show={this.state.isDropDownOpen}>
                    <VirtualList
                        width="100%"
                        height={dropDownHeight}
                        itemCount={this.state.dataSource.length}
                        itemHeight={this.props.dropDownMenuItemHeight}
                        scrollToIndex={this.state.scrollToIndex}>
                        {
                            (index, style) =>
                            {
                                let data = this.state.dataSource[index];
                                return this.getItem(index, data, style);
                            }
                        }
                    </VirtualList>
                </DropDown>
                {
                    this.props.hint &&
                    <InputHint style={{
                        marginTop: 5,
                    }}>
                        {this.props.hint}
                    </InputHint>
                }
            </InputContainer>
        );
    }
}